import type { IDropdownButtonProps } from "@similarweb/ui-components";
import type { IEllipsisDropdownItemProps } from "@similarweb/ui-components";
import { translate } from "app/filters/i18nFilter";
import { isEmpty } from "lodash";
import FilterSelectAllItem from "app/pages/sales-intelligence/sub-modules/signals/components/filters/shared/FilterDropdown/FilterSelectAllItem/FilterSelectAllItem";
import SignalFilterDropdownLabel from "app/pages/sales-intelligence/sub-modules/signals/components/filters/shared/SignalFilterDropdownLabel/SignalFilterDropdownLabel";
import useSignalFilterDropdownLabel from "app/pages/sales-intelligence/sub-modules/signals/hooks/useSignalFilterDropdownLabel";
import type SignalAdNetwork from "app/pages/sales-intelligence/sub-modules/signals/models/ad-network/SignalAdNetwork";
import { FilterType } from "app/pages/sales-intelligence/sub-modules/signals/models/services/FilterService";
import filterService from "app/pages/sales-intelligence/sub-modules/signals/services/filters/filterService";
import { useCallback } from "react";

import useMultiselectHandler from "../../../hooks/useMultiselectHandler";
import FilterButtonLabel from "../shared/FilterDropdown/FilterButtonLabel/FilterButtonLabel";
import SignalFilterDropdownButton from "app/pages/sales-intelligence/sub-modules/signals/components/filters/shared/FilterDropdown/SignalFilterDropdownButton/SignalFilterDropdownButton";
import FilterDropdownItemWithCheckbox from "../shared/FilterDropdown/FilterDropdownItemWithCheckbox/FilterDropdownItemWithCheckbox";
import FilterMultiselectDropdown from "../shared/FilterMultiselectDropdown/FilterMultiselectDropdown";

type DropdownButtonProps = Pick<IDropdownButtonProps, "isOpen" | "disabled">;

type FilterValue = readonly number[] | undefined;

export type SignalAdNetworksFilterProps = {
    filterKey: string;
    isActive?: boolean;
    selectedValue: FilterValue;
    options: readonly SignalAdNetwork[];
    onValueChange(filterValue: FilterValue): void;
    onToggle?(isOpen: boolean): void;
};

const SignalAdNetworksFilter = (props: SignalAdNetworksFilterProps) => {
    const { filterKey, isActive = true, selectedValue, options, onValueChange, onToggle } = props;

    const isFullSelection = selectedValue?.length === options.length;
    const isEmptySelection = isEmpty(selectedValue);

    const buildFilterName = useCallback(() => {
        const nameBuilder = filterService.getFilterName(FilterType.AdNetworks);

        return nameBuilder.buildName({ adNetworkIds: selectedValue }, options);
    }, [options, selectedValue]);

    const { model: filterLabelModel } = useSignalFilterDropdownLabel({ buildName: buildFilterName });

    const { onSelect } = useMultiselectHandler({
        selectedValue,
        onValueChange,
    });

    const handleChange = useCallback(
        (itemId: number | null, isSelected: boolean) => {
            if (itemId === null) {
                const changedValues = isSelected ? [] : options.map(({ id }) => id);

                onValueChange(changedValues);
            } else {
                onSelect(itemId, isSelected);
            }
        },
        [onSelect, onValueChange, options],
    );

    const renderItem = (item: SignalAdNetwork) => (
        <FilterDropdownItemWithCheckbox
            key={item.id}
            // @ts-expect-error Type number is not assignable to type string
            id={item.id}
        >
            {item.name}
        </FilterDropdownItemWithCheckbox>
    );

    const renderButton = ({ isOpen, disabled }: DropdownButtonProps) => {
        return (
            <SignalFilterDropdownButton
                id={filterKey}
                isOpen={isOpen}
                disabled={disabled}
                hasValue={!isEmptySelection}
            >
                <FilterButtonLabel>
                    <SignalFilterDropdownLabel model={filterLabelModel} />
                </FilterButtonLabel>
            </SignalFilterDropdownButton>
        );
    };

    const renderSelectAllItem = (props: IEllipsisDropdownItemProps) => {
        return (
            <FilterSelectAllItem {...props}>
                {translate(`si.signals.filters.${filterKey}.name.any`)}
            </FilterSelectAllItem>
        );
    };

    return (
        <FilterMultiselectDropdown
            filterKey={filterKey}
            isFullSelection={isFullSelection}
            selectedOptions={selectedValue ?? []}
            buttonComponent={renderButton}
            selectAllComponent={renderSelectAllItem}
            disabled={!isActive}
            processOptions
            virtualize
            hasSearch
            onChange={handleChange}
            onToggle={onToggle}
        >
            {options.map(renderItem)}
        </FilterMultiselectDropdown>
    );
};

export default SignalAdNetworksFilter;
